import React, { useState } from "react";
import { useSelector } from "react-redux";

import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { SelectField } from "../../../common/u1/SelectField";
import { Modal } from "../../../common/Modal";
import { TicketRaisedSuccessfully } from "../../Dashboard/help/TicketRaisedSuccessfully";
import { useSupportTicketMutation } from "../../../../store/admin/adminApiSlice";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { TicketAttachment } from "./TicketAttachment";
import {
  SUPPORT_SUBJECTS,
  WORD_LIMIT,
  countWords,
  isOverWordLimit,
  isTooLarge,
} from "./supportTickets";

/* Raise a ticket. TRANSCRIBED from help/HelpAndSupport.jsx: the same subject list, the same five fields on the request (subject, message, email_address,... */
export const RaiseTicketCard = ({ onRaised, className = "" }) => {
  const { userInfo, token } = useSelector((state) => state.auth);
  const [createSupportTicket, { isLoading }] = useSupportTicketMutation();

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [subjectError, setSubjectError] = useState(false);
  const [supportID, setSupportID] = useState(0);
  const [raised, setRaised] = useState(false);

  const words = countWords(description);
  const over = isOverWordLimit(description);

  const chooseFile = (picked) => {
    if (!picked) {
      setFile(null);
      setFileError("");
      return;
    }
    /* The design states a 5 MB cap; the live form states none. Reported
       rather than silently dropped, and the server stays the authority. */
    if (isTooLarge(picked)) {
      setFile(null);
      setFileError("That file is over 5 MB. Please attach a smaller one.");
      return;
    }
    setFileError("");
    setFile(picked);
  };

  /* Transcribed verbatim, including that the subject is the only requirement
     and that the upload happens before the ticket is created. */
  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (subject === "") {
      setSubjectError(true);
      return;
    }

    try {
      const base = {
        subject,
        message: description,
        email_address: userInfo?.email,
        individualID: userInfo?.individual?.[0]?.individualID,
        status: "raised",
      };

      let payload = base;

      if (file) {
        const uploaded = await uploadFileToS3(
          uploadFilePath?.supportScreenshot,
          file,
          token,
          "",
          "tickets"
        );
        const formData = new FormData();
        Object.entries(base).forEach(([k, v]) => formData.append(k, v));
        formData.append("screenshot", uploaded?.url);
        payload = formData;
      }

      const res = await createSupportTicket({ data: payload, token }).unwrap();

      if (res) {
        setSupportID(res.supportID);
        setRaised(true);
        setDescription("");
        setSubject("");
        setFile(null);
        /* Lets the page refresh the activity list, which the live screen gets
           for free because it reads the session user. */
        if (onRaised) onRaised(res);
      }
    } catch {
      /* The live handler swallows this entirely, so a failed ticket looks like
         nothing happening. Surfaced — rulebook §5. */
      setFileError("Couldn't raise the ticket. Please try again.");
    } finally {
      setSubjectError(false);
    }
  };

  return (
    <section
      className={cn(
        "rounded-[18px] border border-[var(--line)] bg-white p-5 sm:p-[26px]",
        "shadow-[0_1px_2px_rgba(20,40,60,.03)]",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="h-5 w-1 shrink-0 rounded-sm bg-[var(--coral)]" />
        <h3 className="m-0 text-[19px] font-bold text-[var(--ink)] font-sans">
          Raise a ticket
        </h3>
      </div>
      <p className="mt-1.5 mb-5 text-[13.5px] text-[var(--muted)] font-sans">
        Tell us the problem you are facing and we will pick it up from here.
      </p>

      <form onSubmit={submit} noValidate>
        <SelectField
          id="u1-ticket-subject"
          label="Subject"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Select a subject"
          options={SUPPORT_SUBJECTS}
          error={subjectError ? "Please choose a subject." : undefined}
        />

        <div className="mt-5">
          <div className="mb-1.5 flex flex-wrap items-baseline gap-2">
            <label
              htmlFor="u1-ticket-message"
              className="text-[13px] font-semibold text-[var(--ink)] font-sans"
            >
              Describe the problem you are facing
            </label>
            <span className="text-[12px] text-[var(--muted)] font-sans">
              max {WORD_LIMIT} words
            </span>
          </div>

          <textarea
            id="u1-ticket-message"
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter a description…"
            className={cn(
              "block w-full rounded-xl border bg-white px-3.5 py-3",
              "text-[14px] text-[var(--ink)] font-sans outline-none transition-colors",
              "placeholder:text-[var(--muted-2)]",
              over
                ? "border-[var(--danger)]"
                : "border-[var(--line)] focus:border-[var(--coral)]"
            )}
          />

          {/* Shown, not enforced — see the note at the top of this file. */}
          <p
            className={cn(
              "mt-1.5 mb-0 text-right text-[12px] font-sans",
              over ? "font-semibold text-[var(--danger)]" : "text-[var(--muted)]"
            )}
          >
            {words} / {WORD_LIMIT} words
          </p>
        </div>

        <TicketAttachment
          className="mt-4"
          file={file}
          onSelect={chooseFile}
          error={fileError}
        />

        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-[var(--line)] pt-5">
          <Button type="submit" variant="coralSolid" disabled={isLoading}>
            {isLoading ? "Submitting…" : "Submit ticket"}
          </Button>
          <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--muted)] font-sans">
            Tickets are answered 9 AM – 6 PM GST, Monday to Friday. Urgent payment
            issues are picked up first.
          </p>
        </div>
      </form>

      {/* Reused unchanged — the live confirmation, keyed by the same id. */}
      {raised && (
        <Modal modalHandle={() => setRaised(false)}>
          <TicketRaisedSuccessfully close={() => setRaised(false)} supportID={supportID} />
        </Modal>
      )}
    </section>
  );
};

export default RaiseTicketCard;
